import React, { forwardRef } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { shouldNotForwardProps } from '@polestar/component-warehouse-react/helpers/shouldForwardProps';
import { MainAxisAlignments } from '@polestar/component-warehouse-react/tokens/alignments/Alignments';
import Column from '@polestar/component-warehouse-react/components/column/Column';
import Section, { MediaSection, SectionInner, } from '@polestar/component-warehouse-react/components/section/Section';
import Paragraph from '@polestar/component-warehouse-react/components/paragraph/Paragraph';
import Grid from '@polestar/component-warehouse-react/components/grid/Grid';
import GridColumn from '@polestar/component-warehouse-react/components/grid/GridColumn';
import Image from '@polestar/component-warehouse-react/components/image/Image';
import { DarkThemeProvider, LightThemeProvider, } from '@polestar/component-warehouse-react/components/theme-providers/ThemeProviders';
import { responsiveStyles } from '@polestar/component-warehouse-react/helpers/responsiveStyle';
import DeviceTypes from '@polestar/component-warehouse-react/tokens/device-types/DeviceTypes';
import { HeroSectionCommonButtonGroup, HeroSectionCommonTitleGroup, contentGridCols, } from './HeroSectionCommon';
import { HeroSectionSplitTheme } from '../HeroSection';
import Spacings, { createCustomSpacing, } from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import AspectRatio from '@polestar/component-warehouse-react/components/aspect-ratio/AspectRatio';
import useMediaQuery from '@polestar/component-warehouse-react/hooks/useMediaQuery';
import { useThemeType } from '@polestar/component-warehouse-react/hooks/useTheme';
import { ThemeTypes } from '@polestar/component-warehouse-react/themes/Theme';
import Colors from '@polestar/component-warehouse-react/tokens/colors/Colors';
import Grids from '@polestar/component-warehouse-react/tokens/grids/Grids';
import warnOnce from '@polestar/component-warehouse-react/helpers/warnOnce';
const HeroSectionSplit = forwardRef(({ title, tagline, intro, buttonOne, buttonTwo, mediaAspectRatio, image, sectionTheme, ...props }, ref) => {
    const { name } = useMediaQuery();
    // Get page theme
    const currentThemeType = useThemeType();
    // Warnings for incompatible prop combinations
    if (currentThemeType === ThemeTypes.dark &&
        (sectionTheme === HeroSectionSplitTheme.light || sectionTheme === HeroSectionSplitTheme.dark)) {
        warnOnce('HeroSectionSplit', 'sectionTheme is set to "light" or "dark", but page theme is "dark". When page theme is "dark", sectionTheme is ignored, and the background color is set to "transparent".');
    }
    const Content = () => (React.createElement(SplitContent, { mainAxisAlignment: MainAxisAlignments.center },
        React.createElement(HeroSectionCommonTitleGroup, { title: title, tagline: tagline }),
        React.createElement(Paragraph, null, intro),
        React.createElement(HeroSectionCommonButtonGroup, { buttonOne: buttonOne, buttonTwo: buttonTwo })));
    const ThemeWrapper = sectionTheme === HeroSectionSplitTheme.dark || currentThemeType === ThemeTypes.dark
        ? DarkThemeProvider
        : LightThemeProvider;
    /**
     * Bleed is forced in:
     * sectionTheme === HeroSectionSplitTheme.light ||
     * sectionTheme === HeroSectionSplitTheme.dark
     * Grid is forced in:
     * sectionTheme === HeroSectionSplitTheme.transparent
     */
    const isNonBleed = sectionTheme === HeroSectionSplitTheme.transparent;
    const SectionSplit = isNonBleed ? Section : MediaSection;
    return (React.createElement(SectionSplit, { ...props, ref: ref },
        React.createElement(ThemeWrapper, null,
            React.createElement(HeroSectionInner, { currentThemeType: currentThemeType, sectionTheme: sectionTheme },
                React.createElement(Grid, null,
                    name !== DeviceTypes.mobile && name !== DeviceTypes.tablet && (React.createElement(StyledGridColumn, { span: contentGridCols(!isNonBleed) },
                        React.createElement(Content, null))),
                    React.createElement(StyledGridColumn, { span: { mobile: 4, tablet: 8, desktop: 7, desktopWide: 7 }, offset: {
                            mobile: 0,
                            tablet: 0,
                            desktop: 0,
                            desktopWide: !isNonBleed ? 1 : 0,
                        } },
                        React.createElement(AspectRatio, { ratio: mediaAspectRatio },
                            React.createElement(Image, { ...image })))),
                (name === DeviceTypes.mobile || name === DeviceTypes.tablet) && React.createElement(Content, null)))));
});
HeroSectionSplit.displayName = 'HeroSectionSplit';
export default HeroSectionSplit;
const SplitContent = styled(Column)(css `
  position: relative;
  grid-area: content;
  gap: ${Spacings.medium};
  height: 100%;
  z-index: 2;
`);
const HeroSectionInner = styled(SectionInner, shouldNotForwardProps(true, 'sectionTheme', 'currentThemeType'))(({ sectionTheme, currentThemeType }) => {
    return css(css `
      display: flex;
      flex-direction: column;
      ${responsiveStyles({
        gap: {
            mobile: Spacings.large,
            tablet: Spacings.xLarge,
        },
        paddingBlockStart: {
            mobile: Grids.mobile.gutter,
            tablet: Spacings.large,
            desktop: Spacings.xLarge,
            desktopWide: Spacings.xxxxLarge,
        },
        paddingBlockEnd: {
            mobile: Spacings.large,
            tablet: Spacings.xLarge,
            desktop: Spacings.xLarge,
            desktopWide: Spacings.xxxxLarge,
        },
    })};
      background-color: ${(currentThemeType === ThemeTypes.light &&
        (sectionTheme === HeroSectionSplitTheme.light
            ? Colors.signalWhite
            : sectionTheme === HeroSectionSplitTheme.dark
                ? Colors.black
                : 'inherit')) ||
        (currentThemeType === ThemeTypes.dark || sectionTheme === HeroSectionSplitTheme.transparent
            ? 'transparent'
            : 'inherit')};
    `, sectionTheme === HeroSectionSplitTheme.transparent &&
        css `
        ${responsiveStyles({
            paddingInline: {
                mobile: Spacings.none,
                tablet: Spacings.none,
                desktop: Spacings.none,
                desktopWide: Spacings.none,
            },
            paddingBlockStart: {
                mobile: Spacings.none,
                tablet: Spacings.none,
                desktop: Spacings.none,
                desktopWide: Spacings.none,
            },
            paddingBlockEnd: {
                mobile: Spacings.large,
                tablet: Spacings.xLarge,
                desktop: createCustomSpacing(40 / 16),
                desktopWide: createCustomSpacing(40 / 16),
            },
        })};
      `);
});
const StyledGridColumn = styled(GridColumn)(css `
  margin-block: auto;
`);
